import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { toIsoLocal } from '../lib/dateTime';
import {
  Clock,
  Plus,
  Edit2,
  Trash2,
  Moon,
  Sun,
  Calendar,
  Save,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Play,
  Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';

const WEEKDAYS = [
  { value: 1, label: 'Mo' },
  { value: 2, label: 'Di' },
  { value: 3, label: 'Mi' },
  { value: 4, label: 'Do' },
  { value: 5, label: 'Fr' },
  { value: 6, label: 'Sa' },
  { value: 7, label: 'So' },
];

export default function PayrollRulesTab() {
  const { api } = useAuth();
  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState([]);
  const [wageTypes, setWageTypes] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Dialog states
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  
  // Forms
  const [settingsForm, setSettingsForm] = useState({
    night_start: '20:00',
    night_end: '06:00',
    sunday_carry_until: '04:00',
    sunday_carry_enabled: false,
    rounding_minutes: 15,
    default_wage_type: '0001',
    night_wage_type: '0004',
    sunday_wage_type: '0005',
    holiday_wage_type: '0006',
    max_auto_break_gap_minutes: 60,
  });
  
  const [ruleForm, setRuleForm] = useState({
    name: '',
    priority: 100,
    is_active: true,
    weekdays: [],
    is_holiday: null,
    time_range_start: '',
    time_range_end: '',
    wage_type_code: '0001',
    label: '',
  });
  
  const [previewForm, setPreviewForm] = useState({
     date: toIsoLocal(new Date()),
    start_time: '08:00',
    end_time: '17:00',
    break_minutes: 30,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, rulesRes, wageTypesRes] = await Promise.all([
        api.get('/payroll-rules/settings'),
        api.get('/payroll-rules/rules?include_inactive=true'),
        api.get('/datev/wage-types'),
      ]);
      
      setSettings(settingsRes.data);
      setSettingsForm(prev => ({
        ...prev,
        ...settingsRes.data
      }));
      setRules(rulesRes.data);
      setWageTypes(wageTypesRes.data);
    } catch (error) {
      console.error('Failed to fetch payroll rules:', error);
      toast.error('Fehler beim Laden der Arbeitszeitregeln');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await api.put('/payroll-rules/settings', settingsForm);
      toast.success('Einstellungen gespeichert');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const openRuleDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      const conditions = rule.conditions || {};
      const timeRange = conditions.time_ranges?.[0] || {};
      
      setRuleForm({
        name: rule.name,
        priority: rule.priority,
        is_active: rule.is_active,
        weekdays: conditions.weekdays || [],
        is_holiday: conditions.is_holiday ?? null,
        time_range_start: timeRange.start || '',
        time_range_end: timeRange.end || '',
        wage_type_code: rule.output?.datev_wage_type_code || '0001',
        label: rule.output?.label || '',
      });
    } else {
      setEditingRule(null);
      setRuleForm({
        name: '',
        priority: 100,
        is_active: true,
        weekdays: [],
        is_holiday: null,
        time_range_start: '',
        time_range_end: '',
        wage_type_code: '0001',
        label: '',
      });
    }
    setIsRuleDialogOpen(true);
  };

  const handleRuleSubmit = async (e) => {
    e.preventDefault();
    
    // Build conditions object
    const conditions = {};
    if (ruleForm.weekdays.length > 0) {
      conditions.weekdays = ruleForm.weekdays;
    }
    if (ruleForm.is_holiday !== null) {
      conditions.is_holiday = ruleForm.is_holiday;
    }
    if (ruleForm.time_range_start && ruleForm.time_range_end) {
      conditions.time_ranges = [{
        start: ruleForm.time_range_start,
        end: ruleForm.time_range_end
      }];
    }
    
    const payload = {
      name: ruleForm.name,
      priority: ruleForm.priority,
      conditions,
      output: {
        datev_wage_type_code: ruleForm.wage_type_code,
        label: ruleForm.label || ruleForm.name,
      },
    };
    
    try {
      if (editingRule) {
        await api.put(`/payroll-rules/rules/${editingRule.id}`, {
          ...payload,
          is_active: ruleForm.is_active,
        });
        toast.success('Regel aktualisiert');
      } else {
        await api.post('/payroll-rules/rules', payload);
        toast.success('Regel erstellt');
      }
      setIsRuleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Möchten Sie diese Regel wirklich löschen?')) return;
    try {
      await api.delete(`/payroll-rules/rules/${ruleId}`);
      toast.success('Regel gelöscht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await api.put(`/payroll-rules/rules/${rule.id}`, {
        is_active: !rule.is_active
      });
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Umschalten');
    }
  };

  const handleSetupDefaults = async () => {
    if (!window.confirm('Standard-Regeln erstellen? Bestehende Regeln müssen vorher gelöscht werden.')) return;
    try {
      await api.post('/payroll-rules/setup-defaults');
      toast.success('Standardregeln erstellt');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handlePreview = async () => {
    try {
      const res = await api.post('/payroll-rules/preview', null, {
        params: previewForm
      });
      setPreviewResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Vorschau');
    }
  };

  const toggleWeekday = (day) => {
    setRuleForm(prev => ({
      ...prev,
      weekdays: prev.weekdays.includes(day)
        ? prev.weekdays.filter(d => d !== day)
        : [...prev.weekdays, day].sort((a, b) => a - b)
    }));
  };

  const getPriorityBadge = (priority) => {
    if (priority >= 200) return { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Höchste' };
    if (priority >= 150) return { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'Hoch' };
    if (priority >= 100) return { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'Mittel' };
    if (priority >= 50) return { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Niedrig' };
    return { color: 'bg-muted text-muted-foreground border-border/30', label: 'Basis' };
  };

  const getConditionText = (conditions) => {
    const parts = [];
    if (conditions?.weekdays?.length) {
      const dayNames = conditions.weekdays.map(d => WEEKDAYS.find(w => w.value === d)?.label).join(', ');
      parts.push(`Tage: ${dayNames}`);
    }
    if (conditions?.is_holiday === true) parts.push('Feiertag');
    if (conditions?.is_holiday === false) parts.push('Kein Feiertag');
    if (conditions?.time_ranges?.length) {
      const r = conditions.time_ranges[0];
      parts.push(`${r.start}-${r.end}`);
    }
    return parts.length > 0 ? parts.join(' • ') : 'Keine Bedingungen';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Global Settings */}
      <div className="card-glass p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-foreground">Globale Einstellungen</h3>
              <p className="text-sm text-muted-foreground">Nachtfenster, Rundung und Standard-Lohnarten</p>
            </div>
          </div>
          <Button onClick={handleSaveSettings} disabled={isSaving} className="bg-indigo-500 hover:bg-indigo-600 text-foreground">
            <Save className="w-4 h-4 mr-2" />
            Speichern
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Night Window */}
          <div className="space-y-4 p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <Moon className="w-4 h-4" />
              <span className="font-medium">Nachtfenster</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Von</Label>
                <Input
                  type="time"
                  value={settingsForm.night_start}
                  onChange={(e) => setSettingsForm({ ...settingsForm, night_start: e.target.value })}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Bis</Label>
                <Input
                  type="time"
                  value={settingsForm.night_end}
                  onChange={(e) => setSettingsForm({ ...settingsForm, night_end: e.target.value })}
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>
          </div>

          {/* Sunday Carry */}
          <div className="space-y-4 p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
              <Sun className="w-4 h-4" />
              <span className="font-medium">Sonntags-Weiterzählung</span>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm text-muted-foreground">Aktiviert</Label>
                <Switch
                  checked={settingsForm.sunday_carry_enabled}
                  onCheckedChange={(c) => setSettingsForm({ ...settingsForm, sunday_carry_enabled: c })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Bis (Folgetag)</Label>
                <Input
                  type="time"
                  value={settingsForm.sunday_carry_until}
                  onChange={(e) => setSettingsForm({ ...settingsForm, sunday_carry_until: e.target.value })}
                  disabled={!settingsForm.sunday_carry_enabled}
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>
          </div>

          {/* Rounding */}
          <div className="space-y-4 p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
              <Zap className="w-4 h-4" />
              <span className="font-medium">Rundung</span>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Minutenintervall</Label>
              <Select
                value={settingsForm.rounding_minutes?.toString()}
                onValueChange={(v) => setSettingsForm({ ...settingsForm, rounding_minutes: parseInt(v) })}
              >
                <SelectTrigger className="bg-input border-border text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="1">1 Minute</SelectItem>
                  <SelectItem value="5">5 Minuten</SelectItem>
                  <SelectItem value="15">15 Minuten (0.25h)</SelectItem>
                  <SelectItem value="30">30 Minuten (0.5h)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Implizite Pausenerkennung */}
          <div className="space-y-4 p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <Clock className="w-4 h-4" />
              <span className="font-medium">Implizite Pausenerkennung</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Lücken zwischen Arbeitsblöcken werden automatisch als Pause erkannt.
              Nur Lücken bis zur Obergrenze zählen – längere Unterbrechungen gelten als geteilter Dienst.
            </p>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Max. Lückendauer (Minuten)</Label>
              <Select
                value={settingsForm.max_auto_break_gap_minutes?.toString() || '60'}
                onValueChange={(v) => setSettingsForm({ ...settingsForm, max_auto_break_gap_minutes: parseInt(v) })}
              >
                <SelectTrigger className="bg-input border-border text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="30">30 Minuten</SelectItem>
                  <SelectItem value="45">45 Minuten</SelectItem>
                  <SelectItem value="60">60 Minuten (Standard)</SelectItem>
                  <SelectItem value="90">90 Minuten</SelectItem>
                  <SelectItem value="120">120 Minuten</SelectItem>
                  <SelectItem value="0">Deaktiviert (nur explizite Pausen)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                Beispiel: Bei 60 Min zählt eine 30-Min-Lücke zwischen zwei Blöcken als Pause, 
                eine 90-Min-Lücke jedoch nicht.
              </p>
            </div>
          </div>
        </div>

        {/* Default Wage Types */}
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="text-sm font-medium text-muted-foreground mb-4">Standard-Lohnarten</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: 'default_wage_type', label: 'Normal' },
              { key: 'night_wage_type', label: 'Nacht' },
              { key: 'sunday_wage_type', label: 'Sonntag' },
              { key: 'holiday_wage_type', label: 'Feiertag' },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-1">
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <Select
                  value={settingsForm[key]}
                  onValueChange={(v) => setSettingsForm({ ...settingsForm, [key]: v })}
                >
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {Object.entries(wageTypes).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rules Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-foreground">Zuschlagsregeln</h3>
            <p className="text-sm text-muted-foreground">Regeln für Nacht-, Sonntags- und Feiertagszuschläge</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setIsPreviewDialogOpen(true)}
              variant="outline"
              className="border-border text-muted-foreground"
            >
              <Play className="w-4 h-4 mr-2" />
              Testen
            </Button>
            {rules.length === 0 && (
              <Button onClick={handleSetupDefaults} variant="outline" className="border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                <RefreshCw className="w-4 h-4 mr-2" />
                Standardregeln
              </Button>
            )}
            <Button onClick={() => openRuleDialog()} className="bg-indigo-500 hover:bg-indigo-600 text-foreground">
              <Plus className="w-4 h-4 mr-2" />
              Neue Regel
            </Button>
          </div>
        </div>

        {/* Rules Table */}
        <div className="card-glass overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Aktiv</th>
                <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Priorität</th>
                <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Name</th>
                <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Bedingungen</th>
                <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Lohnart</th>
                <th className="text-right p-4 text-xs font-medium text-muted-foreground uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rules.map((rule) => {
                const priority = getPriorityBadge(rule.priority);
                return (
                  <tr key={rule.id} className={`hover:bg-accent/20 ${!rule.is_active ? 'opacity-50' : ''}`}>
                    <td className="p-4">
                      <Switch
                        checked={rule.is_active}
                        onCheckedChange={() => handleToggleRule(rule)}
                      />
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${priority.color}`}>
                        {rule.priority} ({priority.label})
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-foreground font-medium">{rule.name}</span>
                      {rule.output?.label && rule.output.label !== rule.name && (
                        <span className="text-muted-foreground ml-2">→ {rule.output.label}</span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {getConditionText(rule.conditions)}
                    </td>
                    <td className="p-4">
                      <span className="font-mono text-indigo-600 dark:text-indigo-400">
                        {rule.output?.datev_wage_type_code}
                      </span>
                      <span className="text-muted-foreground ml-2">
                        {wageTypes[rule.output?.datev_wage_type_code]}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openRuleDialog(rule)}
                          className="border-border"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteRule(rule.id)}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {rules.length === 0 && (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Keine Regeln vorhanden</p>
              <p className="text-sm text-muted-foreground mt-1">Erstellen Sie Standardregeln oder fügen Sie eigene hinzu</p>
            </div>
          )}
        </div>
      </div>

      {/* Rule Dialog */}
      <Dialog open={isRuleDialogOpen} onOpenChange={setIsRuleDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editingRule ? 'Regel bearbeiten' : 'Neue Regel'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleRuleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Name</Label>
                <Input
                  value={ruleForm.name}
                  onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                  required
                  placeholder="Nachtzuschlag"
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Priorität</Label>
                <Input
                  type="number"
                  value={ruleForm.priority}
                  onChange={(e) => setRuleForm({ ...ruleForm, priority: parseInt(e.target.value) })}
                  min="0"
                  max="999"
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            {/* Conditions */}
            <div className="space-y-4 p-4 rounded-lg bg-muted/50">
              <h4 className="text-sm font-medium text-muted-foreground">Bedingungen</h4>
              
              {/* Weekdays */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Wochentage</Label>
                <div className="flex gap-2">
                  {WEEKDAYS.map((day) => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleWeekday(day.value)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                        ruleForm.weekdays.includes(day.value)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-white/20'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Holiday */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Feiertag</Label>
                <Select
                  value={ruleForm.is_holiday === null ? 'ignore' : ruleForm.is_holiday.toString()}
                  onValueChange={(v) => setRuleForm({ ...ruleForm, is_holiday: v === 'ignore' ? null : v === 'true' })}
                >
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="ignore">Egal</SelectItem>
                    <SelectItem value="true">Nur an Feiertagen</SelectItem>
                    <SelectItem value="false">Nur an Nicht-Feiertagen</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Time Range */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Zeitfenster (optional)</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    type="time"
                    value={ruleForm.time_range_start}
                    onChange={(e) => setRuleForm({ ...ruleForm, time_range_start: e.target.value })}
                    placeholder="Von"
                    className="bg-input border-border text-foreground"
                  />
                  <Input
                    type="time"
                    value={ruleForm.time_range_end}
                    onChange={(e) => setRuleForm({ ...ruleForm, time_range_end: e.target.value })}
                    placeholder="Bis"
                    className="bg-input border-border text-foreground"
                  />
                </div>
              </div>
            </div>

            {/* Output */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">DATEV Lohnart</Label>
                <Select
                  value={ruleForm.wage_type_code}
                  onValueChange={(v) => setRuleForm({ ...ruleForm, wage_type_code: v })}
                >
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {Object.entries(wageTypes).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Label (Export)</Label>
                <Input
                  value={ruleForm.label}
                  onChange={(e) => setRuleForm({ ...ruleForm, label: e.target.value })}
                  placeholder={ruleForm.name || 'Automatisch'}
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsRuleDialogOpen(false)} className="border-border text-muted-foreground">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-foreground">
                {editingRule ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              Segmentierung testen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Datum</Label>
                <Input
                  type="date"
                  value={previewForm.date}
                  onChange={(e) => setPreviewForm({ ...previewForm, date: e.target.value })}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Von</Label>
                <Input
                  type="time"
                  value={previewForm.start_time}
                  onChange={(e) => setPreviewForm({ ...previewForm, start_time: e.target.value })}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Bis</Label>
                <Input
                  type="time"
                  value={previewForm.end_time}
                  onChange={(e) => setPreviewForm({ ...previewForm, end_time: e.target.value })}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Pause (Min)</Label>
                <Input
                  type="number"
                  value={previewForm.break_minutes}
                  onChange={(e) => setPreviewForm({ ...previewForm, break_minutes: parseInt(e.target.value) || 0 })}
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>
            
            <Button onClick={handlePreview} className="w-full bg-emerald-500 hover:bg-emerald-600 text-foreground">
              <Play className="w-4 h-4 mr-2" />
              Berechnen
            </Button>

            {previewResult && (
              <div className="space-y-4 mt-4">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <div className="flex items-center justify-between">
                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">Gesamtstunden</span>
                    <span className="text-2xl font-mono text-foreground">{previewResult.total_hours}h</span>
                  </div>
                </div>

                {/* Segments */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Segmente</h4>
                  {previewResult.segments.map((seg, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground">{seg.date}</span>
                        <span className="text-foreground font-mono">{seg.start} - {seg.end}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${seg.is_night ? 'bg-amber-500/20 text-amber-400' : 'bg-muted text-muted-foreground'}`}>
                          {seg.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-indigo-600 dark:text-indigo-400">{seg.wage_type}</span>
                        <span className="font-mono text-foreground">{seg.hours}h</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals by wage type */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Summen nach Lohnart</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(previewResult.totals).map(([code, data]) => (
                      <div key={code} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <span className="text-muted-foreground">{data.name}</span>
                        <span className="font-mono text-foreground">{data.hours}h</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
